import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    TextField,
    InputAdornment
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useMemo } from 'react';

const HeaderCell = ({ children, align = 'left' }) => (
    <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textAlign: align }}
    >
        {children}
    </Typography>
);

const RowCell = ({ children, align = 'left', bold = false, selected = false }) => (
    <Typography
        variant="body2"
        fontWeight={bold ? 600 : 400}
        sx={{
            textAlign: align,
            color: selected ? '#fff' : 'text.primary',
            opacity: selected ? 0.95 : 1
        }}
    >
        {children}
    </Typography>
);

const ClinicsListCard = ({ clinics, selectedClinicId, onSelect }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const filteredClinics = useMemo(() => {
        if (!search) return clinics;

        return clinics.filter((clinic) =>
            clinic.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [clinics, search]);

    return (
        <Card
            sx={{
                height: 520,
                borderRadius: 0
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                {/* Title */}
                <Typography variant="h3" fontWeight={700} gutterBottom color="primary" mb={2}>
                    {t('management.clinics')}
                </Typography>

                <TextField
                    size="small"
                    placeholder={t('common.search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                {/* Header */}
                <Box
                    display="grid"
                    gridTemplateColumns="2fr 1fr 1fr"
                    gap={1}
                    mb={1}
                    px={1.5}
                    mt={1}
                >
                    <HeaderCell>{t('clinics.name')}</HeaderCell>
                    <HeaderCell align="right">{t('management.doctors')}</HeaderCell>
                    <HeaderCell align="right">{t('management.totalPatients')}</HeaderCell>
                </Box>

                <Divider sx={{ mb: 1, opacity: 0.6 }} />

                {/* List */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        pr: 0.5,

                        '&::-webkit-scrollbar': {
                            width: 6
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#e0e0e0',
                            borderRadius: 3
                        }
                    }}
                >
                    {filteredClinics.length === 0 && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ mt: 2 }}
                        >
                            {t('common.noResults')}
                        </Typography>
                    )}

                    {filteredClinics.map((clinic) => {
                        const selected = clinic.uuid === selectedClinicId;

                        return (
                            <Box
                                key={clinic.uuid}
                                onClick={() => onSelect(clinic)}
                                sx={{
                                    position: 'relative',
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1.25,
                                    mb: 0.5,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    backgroundColor: selected
                                        ? 'primary.main'
                                        : 'transparent',
                                    color: selected ? '#fff' : 'text.primary',
                                    transition:
                                        'background-color 0.2s ease, box-shadow 0.2s ease',

                                    '&:hover': {
                                        backgroundColor: selected
                                            ? 'primary.dark'
                                            : 'action.hover'
                                    }
                                }}
                            >
                                <RowCell bold selected={selected}>
                                    {clinic.name}
                                </RowCell>

                                <RowCell align="right" selected={selected}>
                                    {clinic.doctors}
                                </RowCell>

                                <RowCell align="right" selected={selected}>
                                    {clinic.patients}
                                </RowCell>
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};

export default ClinicsListCard;
